import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
    X,
    ChevronLeft,
    ChevronRight,
    PlayCircle,
    Image as ImageIcon,
    Maximize2, Minimize2
} from "lucide-react";


import http from '../../Api/http';

const Media = () => {
    const { t, i18n } = useTranslation();
    const language = i18n.language;
    const isRTL = language === "ar" || language === "he";

    const [activeTab, setActiveTab] = useState("gallery"); // gallery | videos
    const [media, setMedia] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAlbum, setSelectedAlbum] = useState(null);
    const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        loadMedias();
    }, [i18n.language]);

    const loadMedias = async () => {
        try {
            setLoading(true);
            const lang = i18n.language || 'ar';
            const res = await http.getJsonAxios(http.actions.Medias.GetMedias, { lang }, t);
            if (res.status === 200 && Array.isArray(res.data)) setMedia(res.data);
            setLoading(false);
        } catch (e) {
            console.error(e);
            setLoading(false);
        }
    };

    const getTitle = (item) => language === "ar" ? item.TitleAr : language === "he" ? item.TitleHe : item.TitleEn;
    const getDescription = (item) => language === "ar" ? item.DescriptionAr : language === "he" ? item.DescriptionHe : item.DescriptionEn;

    const handleKeyDown = useCallback(
        (e) => {
            if (!selectedAlbum) return;
            if (e.key === "Escape") setSelectedAlbum(null);
            if (e.key === "ArrowRight")
                setSelectedPhotoIndex((prev) => (prev + 1) % selectedAlbum.Files.length);
            if (e.key === "ArrowLeft")
                setSelectedPhotoIndex((prev) =>
                    prev === 0 ? selectedAlbum.Files.length - 1 : prev - 1
                );
        },
        [selectedAlbum]
    );

    useEffect(() => {
        if (selectedAlbum) {
            document.body.style.overflow = "hidden";
            window.addEventListener("keydown", handleKeyDown);
        } else document.body.style.overflow = "auto";

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = "auto";
        };
    }, [selectedAlbum, handleKeyDown]);

    const filteredMedia = media.filter((album) =>
        activeTab === "gallery"
            ? album.Files.some(f => f.Type === "image")
            : album.Files.some(f => f.Type === "video")
    );

    return (
        <div className="min-h-screen pt-12 pb-20 bg-background" dir={t("dir")}>
            <div className="container px-4 mx-auto">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center mb-16"
                    >
                        <h1 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900 dark:text-white">
                            {t("media")}
                        </h1>
                        <div className="h-1.5 w-28 bg-accent mx-auto rounded-full" />
                    </motion.div>

                    {/* Tabs */}
                    <div className="flex flex-wrap justify-center mb-16">
                        <div className="flex bg-slate-100 dark:bg-slate-800 rounded-2xl p-1 shadow-inner flex-wrap">
                            {["gallery", "videos"].map((tab) => {
                                const Icon = tab === "gallery" ? ImageIcon : PlayCircle;
                                return (
                                    <button
                                        key={tab}
                                        onClick={() => setActiveTab(tab)}
                                        className={`flex items-center px-4 sm:px-6 py-2 m-1 rounded-2xl transition-all duration-300 ${activeTab === tab
                                            ? "bg-primary text-white shadow-md scale-105"
                                            : "text-muted-foreground hover:text-primary"
                                            }`}
                                    >
                                        {isRTL ? t(tab) : null}
                                        <Icon className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                        {!isRTL ? t(tab) : null}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Grid */}
                    {loading ? (
                        <div className="text-center">{t("loading")}...</div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6">
                            {filteredMedia.map((album) => {
                                let firstFile = album.Files.find(f =>
                                    activeTab === "gallery" ? f.Type === "image" : f.Type === "video"
                                );

                                const coverUrl =
                                    firstFile?.Type === "image"
                                        ? firstFile.Url
                                        : album.ThumbnailUrl || "/placeholder-video.png";

                                return (
                                    <motion.div
                                        key={album.Id}
                                        whileHover={{ scale: 1.05 }}
                                        className="rounded-3xl overflow-hidden shadow-xl cursor-pointer bg-white dark:bg-slate-800 group"
                                        onClick={() => {
                                            setSelectedAlbum(album);
                                            setSelectedPhotoIndex(
                                                album.Files.indexOf(firstFile) >= 0
                                                    ? album.Files.indexOf(firstFile)
                                                    : 0
                                            );
                                        }}
                                        title={getDescription(album)}
                                    >
                                        <div className="aspect-video overflow-hidden">
                                            <img
                                                src={coverUrl}
                                                alt={getTitle(album)}
                                                className="w-full h-full object-cover group-hover:scale-110 transition duration-700"
                                            />
                                        </div>

                                        <div className="p-6">
                                            <h3 className="font-bold text-xl text-primary mb-2">
                                                {getTitle(album)}
                                            </h3>
                                            <p className="text-muted-foreground line-clamp-2" title={getDescription(album)}>
                                                {getDescription(album)}
                                            </p>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Modal لكل Album */}
            <AnimatePresence>
                {selectedAlbum && (
                    <motion.div
                        className={`fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50
                        ${isFullscreen ? "p-0" : "p-4"}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSelectedAlbum(null)}
                    >

                        <motion.div
                            initial={{ scale: 0.85, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.85, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className={`relative w-full bg-white dark:bg-slate-900 shadow-2xl flex flex-col
                            ${isFullscreen
                                    ? "fixed inset-0 w-screen h-screen max-w-none rounded-none"
                                    : "max-w-full sm:max-w-6xl rounded-3xl"
                                }`}
                            onClick={(e) => e.stopPropagation()}
                        >

                            {/* Close */}
                            <button
                                onClick={() => setSelectedAlbum(null)}
                                className="absolute top-10 right-4 z-50 bg-black/60 text-white p-2 sm:p-3 rounded-full hover:bg-black"
                            >
                                <X />
                            </button>

                            {/* Fullscreen Toggle */}
                            <button
                                onClick={() => setIsFullscreen(!isFullscreen)}
                                className="absolute top-10 right-16 z-50 bg-black/60 text-white p-2 sm:p-3 rounded-full hover:bg-black"
                            >
                                {isFullscreen ? <Minimize2 /> : <Maximize2 />}
                            </button>

                            {/* Navigation Left */}
                            <button
                                onClick={() =>
                                    setSelectedPhotoIndex((prev) =>
                                        prev === 0
                                            ? selectedAlbum.Files.length - 1
                                            : prev - 1
                                    )
                                }
                                className="absolute left-2 sm:left-4 top-1/3 sm:top-1/2 -translate-y-1/2 z-40 bg-black/60 text-white p-2 sm:p-3 rounded-full hover:bg-black"
                            >
                                <ChevronLeft />
                            </button>

                            {/* Navigation Right */}
                            <button
                                onClick={() =>
                                    setSelectedPhotoIndex((prev) =>
                                        (prev + 1) % selectedAlbum.Files.length
                                    )
                                }
                                className="absolute right-2 sm:right-4 top-1/3 sm:top-1/2 -translate-y-1/2 z-40 bg-black/60 text-white p-2 sm:p-3 rounded-full hover:bg-black"
                            >
                                <ChevronRight />
                            </button>

                            {/* Media */}
                            <div className="flex-1 bg-black flex items-center justify-center overflow-hidden">

                                {selectedAlbum.Files[selectedPhotoIndex]?.Type === "video" ? (

                                    <video
                                        key={selectedAlbum.Files[selectedPhotoIndex].Url}
                                        controls
                                        autoPlay
                                        playsInline
                                        className="max-w-full max-h-full object-contain"
                                    >
                                        <source
                                            src={selectedAlbum.Files[selectedPhotoIndex].Url}
                                            type="video/mp4"
                                        />
                                    </video>

                                ) : (

                                    <img
                                        src={selectedAlbum.Files[selectedPhotoIndex].Url}
                                        alt=""
                                        className="max-w-full max-h-[85vh] object-contain"
                                    />

                                )}

                            </div>

                            {/* Description */}
                            {!isFullscreen && (
                                <div className="p-6 sm:p-8">

                                    <h3 className="text-2xl font-bold text-primary mb-3">
                                        {getTitle(selectedAlbum)}
                                    </h3>

                                    <p className="text-muted-foreground">
                                        {t("mediaFile")} {selectedPhotoIndex + 1} {t("from")} {selectedAlbum.Files.length}
                                    </p>

                                </div>
                            )}

                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
export default Media;